import { collection, query, where, orderBy, limit, onSnapshot, addDoc, doc, updateDoc, serverTimestamp, type FirestoreError } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config';
import { handleFirebaseError } from '../error-handling';
import { getOnlineStatus } from '../persistence';
import type { Message } from '@/types/support';
import { softDeletePatch, type ReplyTo } from '@/lib/groups/chatMessage';

// Cache configuration
const MESSAGES_CACHE_KEY = 'groupMessages';
const CACHE_EXPIRY = 1000 * 60 * 5; // 5 minutes

async function getCachedMessages(groupId: string): Promise<Message[]> {
  try {
    const cache = localStorage.getItem(`${MESSAGES_CACHE_KEY}_${groupId}`);
    if (!cache) return [];
    
    const { messages, timestamp } = JSON.parse(cache);
    const isExpired = Date.now() - timestamp > CACHE_EXPIRY;
    
    return isExpired ? [] : messages;
  } catch {
    return [];
  }
}

async function cacheMessages(groupId: string, messages: Message[]) {
  try {
    localStorage.setItem(`${MESSAGES_CACHE_KEY}_${groupId}`, JSON.stringify({
      messages,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error caching messages:', error);
  }
}

export function subscribeToGroupMessages(
  groupId: string,
  onUpdate: (messages: Message[], info?: { hasMore: boolean }) => void,
  onError?: (error: Error) => void,
  // The newest `limitCount` messages only (lib/groups/chatPaging, same as
  // mobile); the chat widens it to load older pages. Omitted: all, as before.
  limitCount?: number
) {
  console.log('Subscribing to messages for group:', groupId);

  // First try to get cached messages
  getCachedMessages(groupId).then(messages => {
    if (messages.length > 0) {
      console.log('Found cached messages:', messages.length);
      onUpdate(messages);
    }
  });

  // Create a query that only filters by groupId
  // Paged: newest first with a limit (index groupId + createdAt desc); the
  // messages are put back in time order below.
  const messagesQuery = limitCount
    ? query(
        collection(db, 'groupMessages'),
        where('groupId', '==', groupId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      )
    : query(
        collection(db, 'groupMessages'),
        where('groupId', '==', groupId)
      );

  return onSnapshot(
    messagesQuery,
    async (snapshot) => {
      console.log('Received snapshot with docs:', snapshot.docs.length);
      const messages = snapshot.docs
        .map(doc => {
          // A message just sent from here has a pending serverTimestamp:
          // read the local estimate rather than null (mobile does the same).
          const data = doc.data({ serverTimestamps: 'estimate' });
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() ?? new Date(),
          };
        })
        .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()) as Message[];
      
      console.log('Processed messages:', messages.length);
      await cacheMessages(groupId, messages);
      onUpdate(messages, { hasMore: limitCount ? snapshot.size >= limitCount : false });
    },
    (error) => {
      console.error('Error in messages subscription:', error);
      handleFirebaseError(error);
      
      // Try to serve cached messages on error
      getCachedMessages(groupId).then(messages => {
        if (messages.length > 0) {
          onUpdate(messages);
        }
      });
      
      if (onError) onError(error);
    }
  );
}

export async function sendGroupMessage(message: {
  text: string;
  userId: string;
  userName: string;
  groupId: string;
  imageUrl?: string;
  messageType?: 'text' | 'image' | 'imageWithText';
  replyTo?: ReplyTo | null;
}) {
  if (!getOnlineStatus()) {
    throw new Error('Cannot send messages while offline');
  }

  try {
    console.log('Sending message:', message);
    
    // Build the document data, excluding undefined values
    const messageData: any = {
      text: message.text,
      userId: message.userId,
      userName: message.userName,
      groupId: message.groupId,
      messageType: message.messageType || (message.imageUrl ? (message.text.trim() ? 'imageWithText' : 'image') : 'text'),
      // Server clock, not the sender's: a device with a wrong clock used to
      // put its messages out of order (same as namat-mobile#82).
      createdAt: serverTimestamp(),
      status: 'sent' // Add status field for better tracking
    };
    
    // Only include imageUrl if it exists and is not empty
    if (message.imageUrl && message.imageUrl.trim()) {
      messageData.imageUrl = message.imageUrl;
    }
    
    // Only include replyTo when replying (contract shared with mobile).
    if (message.replyTo) {
      messageData.replyTo = message.replyTo;
    }

    const docRef = await addDoc(collection(db, 'groupMessages'), messageData);
    console.log('Message sent successfully:', docRef.id);
    return true;
  } catch (error: any) {
    console.error('Error sending message:', error);
    handleFirebaseError(error);
    return false;
  }
}

/**
 * Soft-delete a group message: the doc stays (so replies and counts keep
 * their anchor) and is updated to { deleted: true, text: '', imageUrl: null,
 * audioUrl: null, deletedAt, deletedBy } — the contract mobile writes too.
 * Who may do it is checked by the caller (canDeleteChatMessage).
 */
export async function softDeleteGroupMessage(messageId: string, deletedBy: string): Promise<boolean> {
  try {
    await updateDoc(doc(db, 'groupMessages', messageId), softDeletePatch(deletedBy, serverTimestamp()));
    return true;
  } catch (error) {
    console.error('Error deleting message:', error);
    handleFirebaseError(error as FirestoreError);
    return false;
  }
}

export async function uploadImage(file: File, groupId: string, userId: string): Promise<string> {
  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('Image size must be less than 5MB');
    }

    // Create unique filename
    const timestamp = Date.now();
    const fileExtension = file.name.split('.').pop();
    const fileName = `${timestamp}_${userId}.${fileExtension}`;
    
    // Upload to Firebase Storage
    const imageRef = ref(storage, `groupMessages/${groupId}/${fileName}`);
    const snapshot = await uploadBytes(imageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
}